// Tenant Admin Portal - User and Department Management
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { QK } from '../../lib/queryKeys';
import { fetchTenantAdminUsers, fetchTenantUserCapacity, fetchTenantDepartments, fetchViewerAccess as fetchViewerAccessFn } from '../../lib/queryFunctions';
import { useAuth } from '../../context/AuthContext';
import { useOnboarding } from '../../context/OnboardingContext';
import OnboardingBeacon from '../../components/onboarding/OnboardingBeacon';
import SidebarLayout from '../../components/layout/SidebarLayout';
import { useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';
import { Users, Briefcase, Plus, MoreVertical, Edit, Trash2, UserCog, Loader2, Copy, CheckCircle, Building2, FileText, TrendingUp, DollarSign, Mail, Sparkles, Eye, EyeOff, UserX, Search, Check, Bell, Clock, Database } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';
import TenantDetailsTab from '../../components/tenant/TenantDetailsTab';
import InvoiceSettingsTab from '../../components/tenant/InvoiceSettingsTab';
import AccountRatingTab from '../../components/tenant/AccountRatingTab';
import ContractTermsTab from '../../components/tenant/ContractTermsTab';
import EmailTemplatesTab from '../../components/tenant/EmailTemplatesTab';
import FeatureRequestTab from '../../components/tenant/FeatureRequestTab';
import MarketingCampaignsTab from '../../components/tenant/MarketingCampaignsTab';
import AuditLogViewer from '../../components/admin/AuditLogViewer';
import DSUAutomationTab from '../../components/admin/DSUAutomationTab';
import RepositorySyncTab from '../../components/admin/RepositorySyncTab';
import FarmFolioLocationsTab from '../../components/farmfolio/FarmFolioLocationsTab';
import { useConfirm } from '../../components/common/ConfirmDialog';

const StorageUsageCard = () => {
  const [usage, setUsage] = React.useState(null);
  React.useEffect(() => {
    api.get('/files/storage/usage').then(res => setUsage(res.data)).catch(() => {});
  }, []);

  if (!usage) return null;

  const totalMB = (usage.total_bytes / (1024 * 1024)).toFixed(1);
  const totalGB = (usage.total_bytes / (1024 * 1024 * 1024)).toFixed(2);
  const capGB = usage.cap_bytes ? (usage.cap_bytes / (1024 * 1024 * 1024)).toFixed(0) : null;
  const pct = usage.cap_bytes ? Math.min(100, (usage.total_bytes / usage.cap_bytes) * 100).toFixed(1) : 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <DollarSign className="w-5 h-5" />
          Storage Usage
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex justify-between items-end">
          <div>
            <p className="text-3xl font-bold">{totalGB < 1 ? `${totalMB} MB` : `${totalGB} GB`}</p>
            <p className="text-sm text-muted-foreground">{usage.file_count} files</p>
          </div>
          {capGB && !usage.unlimited && (
            <p className="text-sm text-muted-foreground">of {capGB} GB</p>
          )}
          {usage.unlimited && (
            <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-medium">
              <Check className="w-3 h-3" /> Unlimited Storage
            </span>
          )}
        </div>
        {!usage.unlimited && capGB && (
          <>
            <div className="w-full bg-muted rounded-full h-3">
              <div
                className={`h-3 rounded-full transition-[width] ${pct > 90 ? 'bg-red-500' : pct > 70 ? 'bg-yellow-500' : 'bg-primary'}`}
                style={{ width: `${Math.min(100, pct)}%` }}
              />
            </div>
            <p className="text-xs text-muted-foreground">{pct}% used</p>
            {pct > 80 && (
              <div className="p-3 bg-yellow-50 border border-yellow-200 rounded text-sm">
                <p className="font-medium text-yellow-800">Storage is getting full</p>
                <p className="text-yellow-700 text-xs mt-1">
                  Contact Barn Door Software to upgrade to Unlimited Storage.
                </p>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

const TenantAdminPage = () => {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const location = useLocation();
  const { user } = useAuth();
  const { completeItem } = useOnboarding();
  const tabFromUrl = new URLSearchParams(location.search).get('tab');
  const [activeTab, setActiveTab] = useState(tabFromUrl || 'users');
  const qc = useQueryClient();

  // Keep the active tab in sync when the URL query param changes
  useEffect(() => {
    const tab = new URLSearchParams(location.search).get('tab');
    if (tab) setActiveTab(tab);
  }, [location.search]);

  // ── Server state ──────────────────────────────────────────────────────────
  const { data: users = [], isLoading: usersLoading } = useQuery({
    queryKey: QK.tenantAdminUsers(),
    queryFn: fetchTenantAdminUsers(),
    onError: () => toast.error('Failed to load users'),
  });

  const { data: userCapacity = null } = useQuery({
    queryKey: QK.tenantUserCapacity(),
    queryFn: fetchTenantUserCapacity(),
  });

  const { data: departments = [], isLoading: deptsLoading } = useQuery({
    queryKey: QK.tenantDepartments(),
    queryFn: fetchTenantDepartments(),
    onError: () => toast.error('Failed to load departments'),
  });

  // Viewer-access — lazy: only fires when the tab is opened
  const [viewerTabOpened, setViewerTabOpened] = useState(false);
  const { data: viewerAccessData = [], isLoading: viewerAccessLoading, refetch: refetchViewerAccess } = useQuery({
    queryKey: QK.viewerAccess(),
    queryFn: async () => {
      const entitiesRes = await api.get('/crm-entities');
      const entities = entitiesRes.data || [];
      const allData = [];
      for (const entity of entities) {
        const approvedIds = entity.approved_owner_ids || [];
        const pendingIds = entity.pending_owner_ids || [];
        const allIds = [...new Set([...approvedIds, ...pendingIds])];
        if (allIds.length > 0) {
          try {
            const ownersRes = await api.get(`/system-users/owner-users?ids=${allIds.join(',')}`);
            for (const owner of (ownersRes.data || [])) {
              allData.push({
                ...owner,
                entity_id: entity.id,
                entity_name: entity.system_name,
                access_key: entity.access_key,
                status: approvedIds.includes(owner.id) ? 'approved' : 'pending',
              });
            }
          } catch { /* ignore */ }
        }
      }
      return allData;
    },
    enabled: viewerTabOpened,
  });

  const invalidateUsers      = () => qc.invalidateQueries({ queryKey: QK.tenantAdminUsers() });
  const invalidateDepts      = () => qc.invalidateQueries({ queryKey: QK.tenantDepartments() });
  const invalidateViewerAccess = () => qc.invalidateQueries({ queryKey: QK.viewerAccess() });

  // Viewer search UI state
  const [viewerSearch, setViewerSearch] = useState('');

  // ── UI state ──────────────────────────────────────────────────────────────
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userForm, setUserForm] = useState({
    email: '',
    first_name: '',
    last_name: '',
    password: '',
    roles: [],
    department: '',
    has_sensitive_access: false,
    has_financial_access: false,
    is_team_leader: false,
    team_leader_department: ''
  });
  const [generatedPassword, setGeneratedPassword] = useState('');
  const [passwordCopied, setPasswordCopied] = useState(false);
  
  // Departments UI state
  const [deptDialogOpen, setDeptDialogOpen] = useState(false);
  const [editingDept, setEditingDept] = useState(null);
  const [deptForm, setDeptForm] = useState({ name: '', team_leader_id: '' });
  
  const [saving, setSaving] = useState(false);

  const roleOptions = [
    { value: 'tenant_system_admin', label: 'System Admin' },
    { value: 'tenant_acct_manager', label: 'Account Manager' },
    { value: 'tenant_acct_admin', label: 'Account Admin' },
    { value: 'tenant_team_member', label: 'Team Member' },
    { value: 'tenant_team_leader', label: 'Team Leader' },
    { value: 'tenant_card_runner', label: 'Card Runner' },
    { value: 'tenant_sale_clerk', label: 'Sale Clerk' },
    { value: 'tenant_farm_manager', label: 'Farm Manager' },
    { value: 'tenant_other', label: 'Other (View Only)' }
  ];

  const handleCreateUser = () => {
    setEditingUser(null);
    setUserForm({
      email: '',
      first_name: '',
      last_name: '',
      password: '',
      roles: ['tenant_other'],
      department: '',
      has_sensitive_access: false,
      has_financial_access: false,
      is_team_leader: false,
      team_leader_department: ''
    });
    setGeneratedPassword('');
    setPasswordCopied(false);
    setUserDialogOpen(true);
    completeItem('invite_user');
  };

  const handleEditUser = (userToEdit) => {
    setEditingUser(userToEdit);
    setUserForm({
      email: userToEdit.email,
      first_name: userToEdit.first_name,
      last_name: userToEdit.last_name,
      password: '',
      roles: userToEdit.roles || [],
      department: userToEdit.department || '',
      has_sensitive_access: userToEdit.has_sensitive_access || false,
      has_financial_access: userToEdit.has_financial_access || false,
      is_team_leader: userToEdit.is_team_leader || false,
      team_leader_department: userToEdit.team_leader_department || ''
    });
    setUserDialogOpen(true);
  };

  const handleSaveUser = async () => {
    if (!userForm.email || !userForm.first_name || !userForm.last_name) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (userForm.roles.length === 0) {
      toast.error('Please select at least one role');
      return;
    }

    setSaving(true);
    try {
      if (editingUser) {
        // Update user
        const updateData = {
          first_name: userForm.first_name,
          last_name: userForm.last_name,
          email: userForm.email,
          roles: userForm.roles,
          department: userForm.department || null,
          has_sensitive_access: userForm.has_sensitive_access,
          has_financial_access: userForm.has_financial_access,
          is_team_leader: userForm.is_team_leader,
          team_leader_department: userForm.team_leader_department || null
        };
        
        if (userForm.password) {
          updateData.password = userForm.password;
        }
        
        await api.put(`/tenant-admin/users/${editingUser.id}`, updateData);
        toast.success('User updated successfully');
      } else {
        // Create user
        const response = await api.post('/tenant-admin/users', userForm);
        // Show generated password if one was created
        if (response.data?.temp_password) {
          setGeneratedPassword(response.data.temp_password);
          toast.success('User created! Please save the temporary password.');
          completeItem('invite_user');
        } else {
          toast.success('User created successfully');
          completeItem('invite_user');
          setUserDialogOpen(false);
        }
      }
      
      // Close dialog for edits
      if (editingUser) {
        setUserDialogOpen(false);
      }
      
      invalidateUsers();
    } catch (error) {
      console.error('Error saving user:', error);
      toast.error(error.response?.data?.detail || 'Failed to save user');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!await confirm({ title: 'Delete Team Member', description: 'Are you sure you want to delete this user? This action cannot be undone.', destructive: true })) return;

    try {
      await api.delete(`/tenant-admin/users/${userId}`);
      toast.success('User deleted successfully');
      invalidateUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleCreateDept = () => {
    setEditingDept(null);
    setDeptForm({ name: '', team_leader_id: '' });
    setDeptDialogOpen(true);
  };

  const handleEditDept = (dept) => {
    setEditingDept(dept);
    setDeptForm({
      name: dept.name,
      team_leader_id: dept.team_leader_id || ''
    });
    setDeptDialogOpen(true);
  };

  const handleSaveDept = async () => {
    if (!deptForm.name.trim()) {
      toast.error('Department name is required');
      return;
    }

    setSaving(true);
    try {
      if (editingDept) {
        await api.put(`/tenant-admin/departments/${editingDept.id}`, deptForm);
        toast.success('Department updated successfully');
      } else {
        await api.post('/tenant-admin/departments', deptForm);
        toast.success('Department created successfully');
      }

      setDeptDialogOpen(false);
      invalidateDepts();
      invalidateUsers(); // Refresh in case team leaders changed
    } catch (error) {
      console.error('Error saving department:', error);
      toast.error(error.response?.data?.detail || 'Failed to save department');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteDept = async (deptId) => {
    if (!await confirm({ title: 'Delete Department', description: 'Are you sure you want to delete this department?', destructive: true })) return;

    try {
      await api.delete(`/tenant-admin/departments/${deptId}`);
      toast.success('Department deleted successfully');
      invalidateDepts();
    } catch (error) {
      console.error('Error deleting department:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete department');
    }
  };

  const copyPassword = () => {
    navigator.clipboard.writeText(generatedPassword);
    setPasswordCopied(true);
    toast.success('Password copied to clipboard');
    setTimeout(() => setPasswordCopied(false), 2000);
  };

  const getRoleLabel = (roleValue) => {
    const role = roleOptions.find(r => r.value === roleValue);
    return role ? role.label : roleValue;
  };

  return (
    <SidebarLayout>
      <div className="space-y-6">
        <div>
          <h1 className="font-serif text-3xl font-semibold">Admin Settings</h1>
          <p className="text-muted-foreground mt-1">Manage your team and departments</p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <div className="overflow-x-auto pb-0.5">
          <TabsList className="h-auto flex-wrap gap-1 sm:flex-nowrap whitespace-nowrap">
            <TabsTrigger value="users">
              <Users className="w-4 h-4 mr-2" /> Team Members
            </TabsTrigger>
            <TabsTrigger value="departments">
              <Briefcase className="w-4 h-4 mr-2" /> Departments
            </TabsTrigger>
            <TabsTrigger value="details">
              <Building2 className="w-4 h-4 mr-2" /> Organization Details
            </TabsTrigger>
            <TabsTrigger value="upgrade">
              <Sparkles className="w-4 h-4 mr-2" /> Manage Plan
            </TabsTrigger>
            <TabsTrigger value="viewer-access" onClick={async () => { setViewerTabOpened(true); }}>
              <Eye className="w-4 h-4 mr-2" /> Viewer Access
            </TabsTrigger>
            <TabsTrigger value="audit-trail">
              <FileText className="w-4 h-4 mr-2" /> Audit Trail
            </TabsTrigger>
            <TabsTrigger value="repository-sync">
              <Database className="w-4 h-4 mr-2" /> Repository Sync
            </TabsTrigger>
            <TabsTrigger value="farm-locations">
              <Database className="w-4 h-4 mr-2" /> Farm Locations
            </TabsTrigger>
          </TabsList>
          </div>

          {/* Users Tab */}
          <TabsContent value="users" className="space-y-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Team Members</CardTitle>
                  {userCapacity && !userCapacity.is_demo && (
                    <p className={`text-sm mt-1 ${userCapacity.is_at_limit ? 'text-destructive font-medium' : 'text-muted-foreground'}`}>
                      {userCapacity.billable_count} / {userCapacity.max_users} users
                      {userCapacity.is_at_limit ? ' — limit reached' : ''}
                      <span className="ml-1 text-xs font-normal text-muted-foreground">(Card Runners & Sale Clerks are unlimited)</span>
                    </p>
                  )}
                  {userCapacity?.is_demo && (
                    <p className="text-sm mt-1 text-muted-foreground">Demo tenant — unlimited users</p>
                  )}
                </div>
                <OnboardingBeacon itemKey="invite_user">
                  <Button onClick={handleCreateUser} className="bg-primary hover:bg-primary/90 text-white">
                    <Plus className="w-4 h-4 mr-2" />
                    Add User
                  </Button>
                </OnboardingBeacon>
              </CardHeader>
              <CardContent>
                {usersLoading ? (
                  <div className="flex justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-primary" />
                  </div>
                ) : users.length === 0 ? (
                  <p className="text-center py-12 text-muted-foreground">No users found</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Role</TableHead>
                        <TableHead>Department</TableHead>
                        <TableHead>Permissions</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {users.map((u) => (
                        <TableRow key={u.id}>
                          <TableCell className="font-medium">
                            {u.first_name} {u.last_name}
                            {u.is_team_leader && (
                              <Badge variant="outline" className="ml-2 text-xs">Team Leader</Badge>
                            )}
                          </TableCell>
                          <TableCell>{u.email}</TableCell>
                          <TableCell>
                            <div className="flex flex-wrap gap-1">
                              {u.roles?.map((role, idx) => (
                                <Badge key={idx} variant="secondary" className="text-xs">
                                  {getRoleLabel(role)}
                                </Badge>
                              ))}
                              {u.roles?.every(r => ['tenant_card_runner','tenant_sale_clerk','tenant_farm_manager'].includes(r)) && (
                                <Badge variant="outline" className="text-xs text-muted-foreground border-dashed">Unlimited</Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-sm">
                            {u.department || '-'}
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              {u.has_financial_access && (
                                <Badge variant="outline" className="text-xs">Financial</Badge>
                              )}
                              {u.has_sensitive_access && (
                                <Badge variant="outline" className="text-xs">Sensitive</Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant={u.is_active ? 'default' : 'secondary'}>
                              {u.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon">
                                  <MoreVertical className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => handleEditUser(u)}>
                                  <Edit className="w-4 h-4 mr-2" />
                                  Edit
                                </DropdownMenuItem>
                                {u.id !== user?.id && (
                                  <DropdownMenuItem 
                                    onClick={() => handleDeleteUser(u.id)}
                                    className="text-red-600"
                                  >
                                    <Trash2 className="w-4 h-4 mr-2" />
                                    Delete
                                  </DropdownMenuItem>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Departments Tab */}
          <TabsContent value="departments" className="space-y-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Departments</CardTitle>
                <Button onClick={handleCreateDept} className="bg-primary hover:bg-primary/90 text-white">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Department
                </Button>
              </CardHeader>
              <CardContent>
                {deptsLoading ? (
                  <div className="flex justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-primary" />
                  </div>
                ) : departments.length === 0 ? (
                  <p className="text-center py-12 text-muted-foreground">No departments created</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Department Name</TableHead>
                        <TableHead>Team Leader</TableHead>
                        <TableHead>Members</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {departments.map((dept) => (
                        <TableRow key={dept.id}>
                          <TableCell className="font-medium">{dept.name}</TableCell>
                          <TableCell className="text-sm">
                            {dept.team_leader_name || '-'}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">{dept.member_count || 0}</Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon">
                                  <MoreVertical className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => handleEditDept(dept)}>
                                  <Edit className="w-4 h-4 mr-2" />
                                  Edit
                                </DropdownMenuItem>
                                <DropdownMenuItem 
                                  onClick={() => handleDeleteDept(dept.id)}
                                  className="text-red-600"
                                >
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tenant Details Tab with Sub-tabs */}
          <TabsContent value="details">
            <Tabs defaultValue="organization" className="space-y-4">
              <div className="overflow-x-auto pb-0.5">
              <TabsList className="h-auto flex-wrap gap-1 sm:flex-nowrap">
                <TabsTrigger value="organization">
                  <Building2 className="w-4 h-4 mr-2" /> Organization
                </TabsTrigger>
                <TabsTrigger value="invoices">
                  <FileText className="w-4 h-4 mr-2" /> Invoices
                </TabsTrigger>
                <TabsTrigger value="rating">
                  <TrendingUp className="w-4 h-4 mr-2" /> Rating
                </TabsTrigger>
                <TabsTrigger value="contracts">
                  <FileText className="w-4 h-4 mr-2" /> Contracts
                </TabsTrigger>
                <TabsTrigger value="emails">
                  <Mail className="w-4 h-4 mr-2" /> Emails
                </TabsTrigger>
                <TabsTrigger value="campaigns">
                  <Mail className="w-4 h-4 mr-2" /> Marketing
                </TabsTrigger>
                <TabsTrigger value="daily-report">
                  <Bell className="w-4 h-4 mr-2" /> Daily Report
                </TabsTrigger>
              </TabsList>
              </div>

              <TabsContent value="organization">
                <TenantDetailsTab tenantId={user?.tenant_id} />
              </TabsContent>

              <TabsContent value="invoices">
                <InvoiceSettingsTab tenantId={user?.tenant_id} />
              </TabsContent>

              <TabsContent value="rating">
                <AccountRatingTab tenantId={user?.tenant_id} />
              </TabsContent>

              <TabsContent value="contracts">
                <ContractTermsTab tenantId={user?.tenant_id} />
              </TabsContent>

              <TabsContent value="emails">
                <EmailTemplatesTab tenantId={user?.tenant_id} />
              </TabsContent>

              <TabsContent value="campaigns">
                <MarketingCampaignsTab tenantId={user?.tenant_id} />
              </TabsContent>

              <TabsContent value="daily-report">
                <DSUAutomationTab />
              </TabsContent>
            </Tabs>
          </TabsContent>

          {/* Feature Request/Upgrade Tab */}
          <TabsContent value="upgrade" className="space-y-4">
            {/* Storage Usage */}
            <StorageUsageCard />
            <FeatureRequestTab tenantId={user?.tenant_id} />
          </TabsContent>

          {/* Viewer Access Tab */}
          <TabsContent value="viewer-access" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Owner Portal Access</CardTitle>
                <div className="relative max-w-xs mt-2">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input placeholder="Search by name, email, or account..." value={viewerSearch} onChange={(e) => setViewerSearch(e.target.value)} className="pl-9 h-8 text-sm" />
                </div>
              </CardHeader>
              <CardContent className="px-4 py-0 pb-4">
                {viewerAccessLoading ? (
                  <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
                ) : (() => {
                  const q = viewerSearch.toLowerCase();
                  const filtered = viewerAccessData.filter(v => !q || [v.entity_name, v.full_name, v.first_name, v.last_name, v.email].some(f => (f || '').toLowerCase().includes(q)));
                  const pending = filtered.filter(v => v.status === 'pending');
                  const approved = filtered.filter(v => v.status === 'approved');

                  return (
                    <div className="space-y-4">
                      {/* Pending Approvals */}
                      {pending.length > 0 && (
                        <div>
                          <p className="text-sm font-semibold mb-2 text-yellow-700">Pending Approval ({pending.length})</p>
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Account</TableHead>
                                <TableHead>Name</TableHead>
                                <TableHead>Email</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead className="text-right">Actions</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {pending.map((v, i) => (
                                <TableRow key={`p-${i}`}>
                                  <TableCell className="text-sm font-medium">{v.entity_name}</TableCell>
                                  <TableCell className="text-sm">{v.full_name || `${v.first_name || ''} ${v.last_name || ''}`}</TableCell>
                                  <TableCell className="text-sm">{v.email}</TableCell>
                                  <TableCell><Badge className="bg-yellow-100 text-yellow-700 pointer-events-none">Pending</Badge></TableCell>
                                  <TableCell className="text-right">
                                    <div className="flex gap-1 justify-end">
                                      <Button variant="outline" size="sm" className="h-7 text-xs text-green-700" onClick={async () => {
                                        try {
                                          await api.post('/owner-portal/approve-access', { entity_id: v.entity_id, owner_user_id: v.id });
                                          toast.success('Access approved');
                                          invalidateViewerAccess();
                                        } catch { toast.error('Failed to approve'); }
                                      }}>
                                        <Check className="w-3 h-3 mr-1" /> Approve
                                      </Button>
                                      <Button variant="ghost" size="sm" className="h-7 text-xs text-red-600" onClick={async () => {
                                        try {
                                          await api.post('/owner-portal/reject-access', { entity_id: v.entity_id, owner_user_id: v.id });
                                          toast.success('Access rejected');
                                          invalidateViewerAccess();
                                        } catch { toast.error('Failed to reject'); }
                                      }}>
                                        <UserX className="w-3 h-3 mr-1" /> Reject
                                      </Button>
                                    </div>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      )}

                      {/* Approved Users */}
                      <div>
                        {pending.length > 0 && <p className="text-sm font-semibold mb-2">Approved ({approved.length})</p>}
                        {filtered.length === 0 ? (
                          <p className="text-sm text-muted-foreground text-center py-8">No owner portal users linked to any account.</p>
                        ) : (
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Account</TableHead>
                                <TableHead>Name</TableHead>
                                <TableHead>Email</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead>Last Login</TableHead>
                                <TableHead className="text-right">Actions</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {approved.map((v, i) => (
                                <TableRow key={`a-${i}`}>
                                  <TableCell className="text-sm font-medium">{v.entity_name}</TableCell>
                                  <TableCell className="text-sm">{v.full_name || `${v.first_name || ''} ${v.last_name || ''}`}</TableCell>
                                  <TableCell className="text-sm">{v.email}</TableCell>
                                  <TableCell><Badge className="bg-green-100 text-green-700 pointer-events-none">Approved</Badge></TableCell>
                                  <TableCell className="text-sm text-muted-foreground">{v.last_login ? new Date(v.last_login).toLocaleString() : 'Never'}</TableCell>
                                  <TableCell className="text-right">
                                    <Button variant="ghost" size="sm" className="h-7 text-xs text-red-600" onClick={async () => {
                                      if (!await confirm({ title: `Disconnect ${v.full_name || v.email} from ${v.entity_name}?`, destructive: true })) return;
                                      try {
                                        await api.post('/owner-portal/reject-access', { entity_id: v.entity_id, owner_user_id: v.id });
                                        toast.success('Access disconnected');
                                        invalidateViewerAccess();
                                      } catch { toast.error('Failed to disconnect'); }
                                    }}>
                                      <UserX className="w-3 h-3 mr-1" /> Disconnect
                                    </Button>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        )}
                      </div>
                    </div>
                  );
                })()}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Audit Trail Tab */}
          <TabsContent value="audit-trail" className="space-y-4">
            <AuditLogViewer tenantId={user?.tenant_id} />
          </TabsContent>

          {/* Repository Sync Tab */}
          <TabsContent value="repository-sync" className="space-y-4">
            <RepositorySyncTab />
          </TabsContent>

          {/* Farm Locations Tab */}
          <TabsContent value="farm-locations" className="space-y-4">
            <FarmFolioLocationsTab />
          </TabsContent>

        </Tabs>
      </div>

      {/* User Create/Edit Dialog */}
      <Dialog open={userDialogOpen} onOpenChange={(open) => {
        if (!open && !generatedPassword) {
          setUserDialogOpen(false);
        }
      }}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingUser ? 'Edit Team Member' : 'Add Team Member'}
            </DialogTitle>
          </DialogHeader>
          
          {generatedPassword ? (
            // Show password after creation
            <div className="space-y-4 py-4">
              <div className="p-4 bg-green-50 border border-green-200 rounded">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                  <div className="flex-1">
                    <p className="font-medium text-green-900">User created successfully!</p>
                    <p className="text-sm text-green-700 mt-1">
                      A welcome email has been sent to <strong>{userForm.email}</strong>
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Temporary Password</Label>
                <div className="flex gap-2">
                  <Input 
                    value={generatedPassword} 
                    readOnly 
                    className="font-mono"
                  />
                  <Button 
                    onClick={copyPassword}
                    variant="outline"
                  >
                    {passwordCopied ? <CheckCircle className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                    {passwordCopied ? 'Copied!' : 'Copy'}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Please save this password - it won&apos;t be shown again. The user should change it after first login.
                </p>
              </div>
            </div>
          ) : (
            // User form
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>First Name *</Label>
                  <Input
                    value={userForm.first_name}
                    onChange={(e) => setUserForm({...userForm, first_name: e.target.value})}
                    placeholder="First name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Last Name *</Label>
                  <Input
                    value={userForm.last_name}
                    onChange={(e) => setUserForm({...userForm, last_name: e.target.value})}
                    placeholder="Last name"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={userForm.email}
                  onChange={(e) => setUserForm({...userForm, email: e.target.value})}
                  placeholder="email@example.com"
                />
              </div>

              <div className="space-y-2">
                <Label>
                  Password {editingUser ? '(leave blank to keep current)' : '(leave blank to auto-generate)'}
                </Label>
                <Input
                  type="password"
                  value={userForm.password}
                  onChange={(e) => setUserForm({...userForm, password: e.target.value})}
                  placeholder={editingUser ? 'Leave blank to keep current' : 'Auto-generate if blank'}
                />
              </div>

              <div className="space-y-2">
                <Label>Roles * (Select one or more)</Label>
                <div className="space-y-2">
                  {roleOptions.map((role) => (
                    <div key={role.value} className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id={`role-${role.value}`}
                        checked={userForm.roles.includes(role.value)}
                        onChange={async (e) => {
                          if (e.target.checked) {
                            setUserForm({...userForm, roles: [...userForm.roles, role.value]});
                          } else {
                            setUserForm({...userForm, roles: userForm.roles.filter(r => r !== role.value)});
                          }
                        }}
                        className="w-4 h-4 rounded border-gray-300"
                      />
                      <label htmlFor={`role-${role.value}`} className="text-sm cursor-pointer">
                        {role.label}
                      </label>
                    </div>
                  ))}
                </div>
                {userForm.roles.includes('tenant_system_admin') && (
                  <p className="text-xs text-amber-600">⚠️ Max 2 System Admins per tenant</p>
                )}
                {userForm.roles.length === 0 && (
                  <p className="text-xs text-red-600">Please select at least one role</p>
                )}
              </div>

              <div className="space-y-2">
                <Label>Department</Label>
                <Select
                  value={userForm.department}
                  onValueChange={(value) => setUserForm({...userForm, department: value})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select department (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    {departments.map((dept) => (
                      <SelectItem key={dept.id} value={dept.name}>
                        {dept.name}
                      </SelectItem>
                    ))}
                    {departments.length === 0 && (
                      <SelectItem value="none" disabled>No departments</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-3 pt-4 border-t">
                <Label className="text-base">Permissions</Label>
                
                <div className="flex items-center justify-between p-3 border rounded">
                  <div>
                    <p className="font-medium text-sm">Financial Access</p>
                    <p className="text-xs text-muted-foreground">View financial data and invoices</p>
                  </div>
                  <Switch
                    checked={userForm.has_financial_access}
                    onCheckedChange={(checked) => setUserForm({...userForm, has_financial_access: checked})}
                  />
                </div>

                <div className="flex items-center justify-between p-3 border rounded">
                  <div>
                    <p className="font-medium text-sm">Sensitive Data Access</p>
                    <p className="text-xs text-muted-foreground">View sensitive account information</p>
                  </div>
                  <Switch
                    checked={userForm.has_sensitive_access}
                    onCheckedChange={(checked) => setUserForm({...userForm, has_sensitive_access: checked})}
                  />
                </div>
              </div>

              {userForm.department && (
                <div className="space-y-3 pt-4 border-t">
                  <div className="flex items-center justify-between p-3 border rounded">
                    <div>
                      <p className="font-medium text-sm">Team Leader for {userForm.department}</p>
                      <p className="text-xs text-muted-foreground">
                        Can view all team tasks, communications, and events
                      </p>
                      <p className="text-xs text-amber-600 mt-1">
                        ⚠️ Only one team leader per department
                      </p>
                    </div>
                    <Switch
                      checked={userForm.is_team_leader}
                      onCheckedChange={(checked) => {
                        setUserForm({
                          ...userForm, 
                          is_team_leader: checked,
                          team_leader_department: checked ? userForm.department : ''
                        });
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            {generatedPassword ? (
              <Button onClick={async () => {
                setUserDialogOpen(false);
                setGeneratedPassword('');
              }}>
                Done
              </Button>
            ) : (
              <>
                <Button variant="outline" onClick={() => setUserDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleSaveUser} disabled={saving}>
                  {saving ? 'Saving...' : 'Save'}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Department Create/Edit Dialog */}
      <Dialog open={deptDialogOpen} onOpenChange={setDeptDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingDept ? 'Edit Department' : 'Create Department'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Department Name *</Label>
              <Input
                value={deptForm.name}
                onChange={(e) => setDeptForm({...deptForm, name: e.target.value})}
                placeholder="e.g., Sales, Operations, Marketing"
              />
            </div>

            <div className="space-y-2">
              <Label>Team Leader</Label>
              <Select
                value={deptForm.team_leader_id}
                onValueChange={(value) => setDeptForm({...deptForm, team_leader_id: value})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select team leader (optional)" />
                </SelectTrigger>
                <SelectContent>
                  {users.filter(u => u.is_active).map((u) => (
                    <SelectItem key={u.id} value={u.id}>
                      {u.first_name} {u.last_name}
                    </SelectItem>
                  ))}
                  {users.filter(u => u.is_active).length === 0 && (
                    <SelectItem value="none" disabled>No active users</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeptDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveDept} disabled={saving}>
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SidebarLayout>
  );
};

export default TenantAdminPage;
